// Pre-flight fetch of the Users export column vocabulary.
//
// The BE endpoint `GET /admin-panel-v2/users/exports/columns` returns the
// exact set of columns the current client can export (cash vs sweepstakes,
// currency label, timezone) plus which columns are always included
// (`data_as_of` etc). This drives the picker in `AsyncExportButton` — we
// no longer hardcode a superset that may include sweepstakes-only columns
// for cash clients.
//
// Cached long enough to survive typical admin workflows (client vocab
// changes only at platform-config time). `staleTime: 30min` matches how
// the app already treats platform-scoped catalogs.

import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { getPrivateInstance } from "../_privateInstance";
import type {
  UsersExportColumnCatalogData,
  UsersExportColumnCatalogResponse,
} from "../../types/users";

export const USERS_EXPORT_COLUMNS_KEY = "USERS_EXPORT_COLUMNS_CATALOG";
export const USERS_EXPORT_COLUMNS_STALE_MS = 30 * 60 * 1000;

/**
 * @internal Exported for tests. Reads the response envelope and returns
 * the catalog body, or `null` when the shape is unusable (empty body,
 * missing `columns` array, etc.).
 */
export const extractCatalog = (
  response: unknown,
): UsersExportColumnCatalogData | null => {
  const body = (
    response as { data?: UsersExportColumnCatalogResponse | undefined }
  )?.data;
  const data = body?.data;
  if (!data || typeof data !== "object") return null;
  if (!Array.isArray(data.columns)) return null;
  return data;
};

export type UseUsersExportColumnCatalogOptions = {
  /** Delay the fetch until the button is actually rendered/visible. */
  readonly enabled?: boolean;
};

export type UsersExportColumnCatalogState = {
  readonly data: UsersExportColumnCatalogData | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
};

/**
 * Fetches the per-client column vocabulary. Prefer calling this at the
 * table level and passing the mapped `columns` down to the shared
 * `<AsyncExportButton>` — the shared component itself stays
 * domain-agnostic and doesn't know about this endpoint.
 */
export function useUsersExportColumnCatalog(
  options: UseUsersExportColumnCatalogOptions = {},
): UsersExportColumnCatalogState {
  const { enabled = true } = options;

  const query = useQuery({
    queryKey: [USERS_EXPORT_COLUMNS_KEY],
    enabled,
    staleTime: USERS_EXPORT_COLUMNS_STALE_MS,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: async ({ signal }: any) => {
      const response = await getPrivateInstance().get(
        API_ROUTES.USERS_EXPORT_COLUMNS,
        { signal },
      );
      return extractCatalog(response);
    },
  });

  return {
    data: (query.data ?? null) as UsersExportColumnCatalogData | null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export default useUsersExportColumnCatalog;
